import type {
  DemoOverlay,
  Folder,
  IDemo,
  IRecording,
  Play,
  Playbook,
  RecordingTypes,
  Tour,
  Workspace,
  WorkspaceFolder,
} from '@carboncopyco/types';
import constate from 'constate';
import { useState } from 'react';
import type { GoToDemoPreviewArgs } from '../../PresentationModals';

export type WorkspacePageProps = {
  rootPath: string;
  goToAllWorkspaces: () => void;
  handleWorkspaceMembersModal: () => void;
  organizationId: string;
  workspaceId: string;
  folderId?: string;
  setFolderId?: (folderId: string) => void;
  setFolder?: (folder: Folder) => void;
  folder?: Folder;
  goToCreateOverlayInFolder?: (
    workspaceId: Workspace['id'],
    folderId: string
  ) => void;
  goToTour: (tour: Tour['id']) => void;
  goToTourPreview: (tourId: Tour['id']) => void;
  handleEditTour: (tourId: Tour['id'], folderId?: Folder['id']) => void;
  handleCreatePresentation?: (demo?: IDemo | DemoOverlay) => void;
  goToDemo: (demoId: string, workspaceId: string) => void;
  goToEditor: (demoId: IDemo['id'], recordingType?: RecordingTypes) => void;
  handleCreateTourFromDemo: (
    id: IDemo['id'],
    originalTourId?: Tour['id'],
    insertIndex?: number,
    folderId?: Folder['id']
  ) => void;
  handleOpenRecorder: (
    workspaceId?: Workspace['id'],
    folderId?: Folder['id']
  ) => void;
  goToDemoPreview: ({
    id,
    firstUrl,
  }: {
    id: IDemo['id'];
    firstUrl: string;
  }) => void;
  goToGiveADemoPreview?: (options?: GoToDemoPreviewArgs) => void;
  goToClone: (id: IRecording['id']) => void;
  handleClonePreview: (clone: IRecording) => void;
  goToCopiedClone: (
    cloneId: IRecording['id'],
    workspaceId: Workspace['id']
  ) => void;
  goToCopiedTour: (tourId: Tour['id'], workspaceId: Workspace['id']) => void;
  createOverlayPath: string;
  goToFolderPage?: (
    id: WorkspaceFolder['id'],
    workspaceId: Workspace['id'],
    search?: string
  ) => void;
  goToArchivedFolderPage: (
    id: WorkspaceFolder['id'],
    workspaceId: Workspace['id'],
    search?: string
  ) => void;
  goToDemoShares: (demoId: IDemo['id']) => void;
  goToTourShares: (tourId: Tour['id']) => void;
  goToCreateOverlayDemoPage: () => void;
  playPresentation?: (play: Play) => Promise<void>;
  previewArchivedDemo: (
    params: Pick<IDemo, 'id' | 'firstUrl' | 'playerToken' | 'isDeleted'>
  ) => void;
  overlayFlag: boolean;
  goToCopiedDemo: (demoId: number, workspaceId: string) => void;
  toursShareAspectRatioEnabled: boolean;
  navigateToWebTourCapture: ({
    appUrl,
    playerToken,
    fileServiceToken,
    tourId,
    originalTourId,
    insertIndex,
  }: {
    appUrl: string;
    playerToken?: string;
    fileServiceToken?: string;
    tourId?: string;
    originalTourId?: string;
    insertIndex?: number;
  }) => void;
  goToPlaybook?: (
    playbookId: Playbook['id'],
    workspaceId?: Workspace['id']
  ) => void;
  desktopDomain?: string;
  goToPlaybookPreview?: (id: string) => Promise<void>;
};

export type WorkspacePageState = WorkspacePageProps;

const useWorkspacePage = (props: WorkspacePageState): WorkspacePageState => {
  const [folder, setFolder] = useState<Folder>();
  const [folderId, setFolderId] = useState<
    Folder['id'] | WorkspaceFolder['id']
  >('0');

  return {
    ...props,
    folderId,
    setFolderId,
    setFolder,
    folder,
  };
};

export const [WorkspacePageProvider, useWorkspacePageProps] =
  constate(useWorkspacePage);
